import {Component, Input, OnInit, OnDestroy, HostListener} from '@angular/core';
import * as moment from 'moment';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import {BatchTransactionsService} from 'src/app/services/batchTransactions.service';
import BatchTransactionsModel from 'src/app/services/batchTransactions.model';
import {PondSectionService} from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {FeedLogService} from 'src/app/services/feedLog.service';
import FeedLogModel from 'src/app/services/feedLog.model';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import {TreatmentLogService} from 'src/app/services/treatmentLog.service';
import TreatmentLogModel from 'src/app/services/treatmentLog.model';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component TreatmentsComponent
 * @selector app-treatments
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `TreatmentsComponent` is responsible for managing and displaying treatment logs and batch transactions
 * related to farm material management. It provides a detailed view of treatments applied to ponds,
 * organized by batch transactions.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches batch treatment transactions based on business, farm site, and section IDs.
 * 2. **Data Presentation**: Displays a list of batch transactions with infinite scrolling support.
 * 3. **Detailed View**: Allows users to view detailed treatment logs for a specific batch transaction in a modal.
 * 4. **Data Conversion**: Provides utility methods to convert units (grams to Kgs) and time formats (24hr to 12hr).
 * 5. **Role-Based Access**: Adapts initialization based on the logged-in user's role (though primarily input-driven).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Can view treatments for their farm.
 *   - **Farm Manager**: Can view treatments for assigned farms.
 * - **Other Categories**:
 *   - **Employee**: Can view treatments for selected businesses.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 * - `LoginUserName`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` captures input IDs (business, site, section) and triggers data loading.
 * 2. **Data Fetching**: `getTreatmentTransactions` calls `BatchTransactionsService` to fetch transaction data.
 * 3. **Display**: Data is loaded into `batchTransactionsList` via `BatchLoader` for pagination.
 * 4. **Detail View**: `getBatchEntryDetailsByBatchNumber` fetches specific treatment logs for a batch and opens a modal.
 *
 * ## UI State Management:
 * - **Loading**: `responseLoader` and `listLoader` manage loading indicators.
 * - **Modals**: Uses Bootstrap modal for displaying detailed treatment logs.
 * - **Pagination**: `BatchLoader` handles infinite scroll pagination.
 */
@Component({
  selector: 'app-treatments',
  templateUrl: './treatments.component.html',
  styleUrls: ['./treatments.component.scss']
})
export class TreatmentsComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of business entities (unused in current logic). */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites (unused in current logic). */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<BatchTransactionsModel>} Master list of batch transactions fetched from the service. */
  public batchTransactions: Array<BatchTransactionsModel>;

  /** @type {Array<BatchTransactionsModel>} The list of batch transactions currently displayed in the UI (paginated). */
  public batchTransactionsList: Array<BatchTransactionsModel>;

  /** @type {BatchTransactionsModel} Single batch transaction model (unused in current logic). */
  public batchTransaction: BatchTransactionsModel;

  /** @type {Array<PondSectionModel>} List of pond sections (unused in current logic). */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<CustomerPondModel>} List of customer ponds (unused in current logic). */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<FeedLogModel>} List of feed logs (unused in current logic). */
  public feedLogs: Array<FeedLogModel>;

  /** @type {FeedLogModel} Single feed log model (unused in current logic). */
  public feedLog: FeedLogModel;

  /** @type {Array<TreatmentLogModel>} List of treatment logs for a specific batch. */
  public treatmentLogs: Array<TreatmentLogModel>;

  /** @type {TreatmentLogModel} Single treatment log model (unused in current logic). */
  public treatmentLog: TreatmentLogModel;

  /** @type {string} Role of the logged-in user. */
  loginPersonType: string;

  /** @type {string} Name of the logged-in user. */
  LoginUserName: string;

  /** @type {string} Name of the logged-in user's business. */
  loginUserBusinessName: string;

  /** @type {boolean} Flag controlling the main loading spinner. */
  public responseLoader: boolean;

  /** @type {string} Farm site ID for filtering (internal). */
  public farm_site_id: string;

  /** @type {string} Business ID for filtering (internal). */
  public business_id: string;

  /** @type {any} Section ID for filtering (internal). */
  public section_id: any;

  // pagination sorting
  /** @type {number} Current page number. Default 1. */
  public currentPageNumber = 1;

  /** @type {number} Items per page. Default 20. */
  public noOfItemsPerPage = 20;

  /** @type {boolean} Flag indicating if search has been clicked. */
  public isSearchClicked: boolean;

  /** @type {string} Business ID parameter for API calls. */
  public paramsbusinessId: string;

  /** @type {string} Farm site ID parameter for API calls. */
  public paramsfarmsiteId: string;

  /** @type {string} Section ID parameter for API calls. */
  public paramssectionId: string;

  /** @type {string} Input property for Business ID. */
  @Input('businessId') public businessId: string;

  /** @type {string} Input property for Site ID. */
  @Input('siteId') public siteId: string;

  /** @type {string} Input property for Section ID. */
  @Input('sectionId') public sectionId: string;

  /** @type {Array<any>} Array to hold treatment logs data for display in modal. */
  public treatmentLogsData = new Array();

  /** @type {any} Timestamp of the selected batch transaction. */
  public selectedBatchTimeStamp: any;

  /** @type {number} Active page number. */
  public activePageNo: number;

  /** @type {boolean} Flag controlling list loader spinner. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Number of records to load per batch scroll. Default 12. */
  public recordsPerBatch = 12;               // Load this many per scroll

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /**
   * Constructor for TreatmentsComponent.
   * Initializes services.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {TreatmentLogService} treatmentLogsService - Service for fetching treatment logs.
   * @param {FeedLogService} feedLogService - Service for feed logs (unused).
   * @param {CustomerPondService} customerPondService - Service for customer ponds (unused).
   * @param {PondSectionService} pondSectionService - Service for pond sections (unused).
   * @param {BatchTransactionsService} batchTransactionService - Service for batch transactions.
   * @param {BusinessEntityService} businessEntityService - Service for business entities (unused).
   * @param {FarmSiteService} farmSitesService - Service for farm sites (unused).
   * @param {Sidenavaction} sidenavaction - Service for side navigation (unused).
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   *
   * @service ToastrService
   * @service TreatmentLogService
   * @service FeedLogService
   * @service CustomerPondService
   * @service PondSectionService
   * @service BatchTransactionsService
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service Sidenavaction
   * @service CommonMethodsService
   */
  constructor(private toasterService: ToastrService, public treatmentLogsService: TreatmentLogService, public feedLogService: FeedLogService, public customerPondService: CustomerPondService, public pondSectionService: PondSectionService, public batchTransactionService: BatchTransactionsService, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public sidenavaction: Sidenavaction,
    public commonMethodsService: CommonMethodsService) {
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes component state.
   * - Captures input IDs (businessId, siteId, sectionId).
   * - Triggers `getTreatmentTransactions` to load initial data.
   */
  public ngOnInit() {
    window.scrollTo(0, 0);
    this.isFiltering = false;
    this.batchTransactions = [];
    this.batchTransactionsList = [];
    this.loginPersonType = localStorage.getItem('loginPersonType');
    /* user login details */
    this.LoginUserName = localStorage.getItem('LoginUserName');
    this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
    /* user login details end */
    this.responseLoader = true;
    this.isSearchClicked = false; // if data is not available
    this.farm_site_id = '';
    this.business_id = '';
    this.paramsbusinessId = this.businessId;
    this.paramsfarmsiteId = this.siteId;
    this.paramssectionId = this.sectionId;
    this.getTreatmentTransactions(this.paramsbusinessId, this.paramsfarmsiteId, this.paramssectionId);
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests.
   */
  public ngOnDestroy(): void {
    this.batchTransactionService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getTreatmentTransactions
   * @async
   * @description Fetches batch treatment transactions based on the provided IDs.
   * Initializes the batch loader for pagination.
   * @param {string} bid - Business ID.
   * @param {string} siteid - Farm Site ID.
   * @param {string} sectionid - Section ID.
   * @side-effect Updates `batchTransactions`, `batchTransactionsList`, `batchLoader`.
   */
  public async getTreatmentTransactions(bid, siteid, sectionid) {
    this.batchTransactions = [];
    this.isFiltering = false;
    try {
      this.batchTransactions = await this.batchTransactionService.getAllBatchTreatmentTransactionsByIds(bid, siteid, sectionid);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      if (this.batchTransactions.length == 0) {
        this.toasterService.warning('Batch data not available.');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.batchTransactionsList : this.batchTransactions,
      (updatedList) => {
        this.batchTransactionsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getBatchEntryDetailsByBatchNumber
   * @async
   * @description Fetches detailed treatment logs for a specific batch number.
   * Opens a modal to display the details.
   * @param {string} batchNumber - The batch number to fetch details for.
   * @param {string} dateTime - The timestamp of the batch transaction.
   * @side-effect Updates `treatmentLogs`, `treatmentLogsData`, `selectedBatchTimeStamp`, opens modal.
   */
  public async getBatchEntryDetailsByBatchNumber(batchNumber, dateTime) {
    try {
      this.selectedBatchTimeStamp = dateTime;
      this.treatmentLogs = await this.treatmentLogsService.getTreatmentLogsByBatchNumber(batchNumber);
      if (this.treatmentLogs.length == 0) {
        this.toasterService.warning('Treatments not available');
        $('#addModal').modal('hide');
      } else {
        this.treatmentLogsData = this.treatmentLogs;
        $('#myModal').modal('show');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method convertToKgs
   * @description Converts a value from grams to kilograms.
   * @param {number} grams - The value in grams.
   * @returns {string} The value in kilograms formatted to 3 decimal places with ' Kgs' suffix.
   */
  public convertToKgs = (grams) => {
    const kgsValue = grams / 1000;
    return kgsValue.toFixed(3) + ' Kgs';
  };

  /**
   * @method convertTo24Hrs
   * @description Converts a timestamp string to 12-hour format with AM/PM.
   * @param {string} timeStamp - The timestamp string (e.g., '2023-01-01T14:30:00').
   * @returns {string} The formatted time string (e.g., '2:30 PM').
   */
  public convertTo24Hrs = (timeStamp) => {
    const time = timeStamp.split('T')[1];
    const _12hrs = moment(time, ['HH:mm']).format('h:mm A');
    return _12hrs;
  };

  /**
   * @method pageChange
   * @description Updates the active page number (unused in current logic).
   * @param {number} pageNum - The new page number.
   */
  public pageChange(pageNum: number) {
    this.activePageNo = pageNum;
  }
}
